/**
 * Authentication API Client
 * 
 * This module provides typed client functions for email/password authentication
 * API routes, replacing server actions with proper API calls that support
 * cookie persistence for session management.
 */

import { UserToken } from '../types/auth.type';

// API Response Types (matching backend interfaces)
interface AuthErrorResponse {
  success: false;
  error: {
    code: string;
    message: string;
    details?: any;
  };
  redirectTo?: string;
}

interface LoginSuccessResponse {
  success: true;
  user: {
    id: string;
    name: string;
    email: string;
    bio?: string;
    avatar?: string;
    alias?: string;
  };
  redirectTo?: string;
}

interface RegisterSuccessResponse {
  success: true;
  message: string;
  user: {
    id: string;
    name: string;
    email: string;
    member_type?: string;
    bio?: string;
    avatar?: string;
    alias?: string;
  };
}

interface UpdateEmailSuccessResponse {
  success: true;
  message: string;
  user?: {
    id: string;
    name: string;
    email: string;
    bio?: string;
    avatar?: string;
    alias?: string;
  };
  confirmationPending?: boolean;
}

interface LogoutSuccessResponse {
  success: true;
  message: string;
}

type AuthResponse = LoginSuccessResponse | RegisterSuccessResponse | UpdateEmailSuccessResponse | LogoutSuccessResponse | AuthErrorResponse;

// Client-side result types
export interface AuthResult {
  success: boolean;
  message?: string;
  user?: UserToken;
  redirectTo?: string;
  confirmationPending?: boolean;
}

/**
 * Login with email and password via API route
 * Replaces handleLogin server action
 */
export async function loginWithEmailPassword(email: string, password: string): Promise<AuthResult> {
  try {
    // console.debug('AUTH_API_CLIENT: Initiating email/password login via API client');
    
    const response = await fetch('/api/auth/email/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // CRITICAL: Ensure cookies are sent and received
      body: JSON.stringify({
        email: email.trim(),
        password: password
      })
    });

    const result: AuthResponse = await response.json();
    
    if (!result.success) {
      const errorResult = result as AuthErrorResponse;
      console.warn('Login failed via API client', { 
        email,
        error: errorResult.error.code,
        message: errorResult.error.message 
      });
      
      return {
        success: false,
        message: errorResult.error.message,
        redirectTo: errorResult.redirectTo
      };
    }

    console.info('Login successful via API client', { 
      email,
      userId: (result as LoginSuccessResponse).user.id 
    });
    
    // Default redirect to dashboard if none specified
    const redirectTo = (result as LoginSuccessResponse).redirectTo || '/dashboard';
    
    return {
      success: true,
      user: (result as LoginSuccessResponse).user as UserToken,
      redirectTo: redirectTo
    };
    
  } catch (error) {
    console.error('Login API call failed', { 
      email,
      error: error instanceof Error ? error.message : String(error)
    });
    
    return {
      success: false,
      message: 'Network error occurred. Please check your connection and try again.'
    };
  }
}

/**
 * Register new user with email and password via API route
 * Replaces handleRegister server action
 */
export async function registerWithEmailPassword(username: string, email: string, password: string): Promise<AuthResult> {
  try {
    // console.debug('Initiating email/password registration via API client');
    
    const response = await fetch('/api/auth/email/register', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // CRITICAL: Ensure cookies are sent and received
      body: JSON.stringify({
        username: username.trim(),
        email: email.trim(),
        password: password
      })
    });

    const result: AuthResponse = await response.json();
    
    if (!result.success) {
      const errorResult = result as AuthErrorResponse;
      console.warn('Registration failed via API client', { 
        email,
        username,
        error: errorResult.error.code,
        message: errorResult.error.message 
      });
      
      return {
        success: false,
        message: errorResult.error.message
      };
    }

    console.info('Registration successful via API client', { 
      email,
      username,
      userId: (result as RegisterSuccessResponse).user.id 
    });
    
    // Default redirect to dashboard after successful registration
    return {
      success: true,
      message: (result as RegisterSuccessResponse).message,
      user: (result as RegisterSuccessResponse).user as UserToken,
      redirectTo: '/dashboard'
    };
    
  } catch (error) {
    console.error('Registration API call failed', { 
      email,
      username,
      error: error instanceof Error ? error.message : String(error)
    });
    
    return {
      success: false,
      message: 'Network error occurred. Please check your connection and try again.'
    };
  }
}

/**
 * Update user email via API route
 * Replaces updateUserEmail server action
 */
export async function updateUserEmailAPI(userId: string, newEmail: string): Promise<AuthResult> {
  try {
    // console.debug('Initiating email update via API client', { userId });
    
    const response = await fetch('/api/auth/email/update', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // CRITICAL: Ensure cookies are sent and received
      body: JSON.stringify({
        userId: userId,
        newEmail: newEmail.trim()
      })
    });

    const result: AuthResponse = await response.json();
    
    if (!result.success) {
      const errorResult = result as AuthErrorResponse;
      console.warn('Email update failed via API client', { 
        userId,
        newEmail,
        error: errorResult.error.code,
        message: errorResult.error.message 
      });
      
      return {
        success: false,
        message: errorResult.error.message
      };
    }

    console.info('Email update successful via API client', { 
      userId,
      newEmail
    });
    
    const updateResult = result as UpdateEmailSuccessResponse;
    
    return {
      success: true,
      message: updateResult.message,
      user: updateResult.user as UserToken,
      confirmationPending: updateResult.confirmationPending
    };
    
  } catch (error) {
    console.error('Email update API call failed', { 
      userId,
      newEmail,
      error: error instanceof Error ? error.message : String(error)
    });
    
    return {
      success: false,
      message: 'Network error occurred. Please check your connection and try again.'
    };
  }
}

/**
 * Logout user via API route
 * Replaces handleLogout server action
 */
export async function logoutUserAPI(): Promise<AuthResult> {
  try {
    // console.debug('Initiating logout via API client');
    
    const response = await fetch('/api/auth/email/logout', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include' // CRITICAL: Ensure cookies are sent and received
    });

    const result: AuthResponse = await response.json();
    
    if (!result.success) {
      const errorResult = result as AuthErrorResponse;
      console.warn('Logout failed via API client', { 
        error: errorResult.error.code,
        message: errorResult.error.message 
      });
      
      return {
        success: false,
        message: errorResult.error.message
      };
    }

    console.info('Logout successful via API client');
    
    return {
      success: true,
      message: (result as LogoutSuccessResponse).message
    };
    
  } catch (error) {
    console.error('Logout API call failed', { 
      error: error instanceof Error ? error.message : String(error)
    });
    
    return {
      success: false,
      message: 'Network error occurred. Please check your connection and try again.'
    };
  }
}

/**
 * Standardized error message mapping for consistent user experience
 */
export function getDisplayErrorMessage(errorCode: string, defaultMessage: string): string {
  const errorMessages: Record<string, string> = {
    // Login errors
    'MISSING_CREDENTIALS': 'Please enter both email and password.',
    'INVALID_CREDENTIALS': 'Invalid email or password. Please try again.',
    'ACCOUNT_DISABLED': 'Your account has been disabled. Please contact support.',
    'PROFILE_NOT_FOUND': 'User profile not found. Please contact support.',
    
    // Registration errors
    'MISSING_FIELDS': 'Please fill in all required fields.',
    'INVALID_EMAIL': 'Please enter a valid email address.',
    'EMAIL_ALREADY_EXISTS': 'An account with this email already exists.',
    'WEAK_PASSWORD': 'Password must be at least 8 characters long.',
    'USER_CREATION_FAILED': 'Failed to create user account. Please try again.',
    
    // Email update errors
    'NO_SESSION': 'Your session has expired. Please log in again.',
    'INVALID_SESSION': 'Your session has expired. Please log in again.',
    'UNAUTHORIZED': 'You are not authorized to perform this action.',
    'EMAIL_UPDATE_FAILED': 'Failed to update email. Please try again.',
    
    // General errors
    'NETWORK_ERROR': 'Network connection failed. Please check your internet connection.',
    'DATABASE_ERROR': 'Database error occurred. Please try again later.',
    'AUTH_SERVICE_ERROR': 'Authentication service error. Please try again.'
  };
  
  return errorMessages[errorCode] || defaultMessage;
}